import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, GraduationCap, Layers } from 'lucide-react';
import { send } from '@/shared/messages';
import type { HomeStats } from '@/shared/messages';
import { daysUntil } from '@/shared/study';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Tag } from '@/components/Badge';
import { Alert } from '@/components/Alert';
import { ProgressRing } from '@/components/Progress';

// Same storage key the Cards queue reads for its chapter-focus filter, so a
// due-chapter chip can hand off "review just this chapter".
const FOCUS_STORAGE_KEY = 'cardsFocusChapterIds';

// The Today view — the design system's flagship home surface: exam countdown,
// session hero with progress ring, due-now chips, and the 3-stat row
// (streak / mastery / learned). Copy follows the brand voice: sentence case,
// mono figures, no emoji.
export function HomeRoute({
  activeTestId,
  refreshKey,
}: {
  activeTestId: string | null;
  refreshKey: number;
}) {
  const navigate = useNavigate();
  const [stats, setStats] = useState<HomeStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    send({ type: 'GET_HOME_STATS', testId: activeTestId ?? undefined })
      .then((r) => {
        if (!cancelled) setStats(r.stats);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [activeTestId, refreshKey]);

  if (error) {
    return (
      <div className="p-4">
        <Alert>Error: {error}</Alert>
      </div>
    );
  }
  if (!stats) {
    return <div className="p-4 text-sm text-slate-500">Loading…</div>;
  }

  const sessionTotal = stats.reviewedToday + stats.dueCount;
  const masteryPct =
    stats.totalCards > 0 ? Math.round((stats.learnedCards / stats.totalCards) * 100) : 0;
  const examDays = stats.examDate !== null ? daysUntil(stats.examDate, Date.now()) : null;

  async function focusChapter(id: string) {
    await chrome.storage.local.set({ [FOCUS_STORAGE_KEY]: [id] });
    navigate('/cards');
  }

  return (
    <div className="space-y-3 p-3 pb-safe-4">
      {stats.testTitle && (
        <div className="flex items-center justify-between gap-2 px-1">
          <div className="flex min-w-0 items-center gap-2">
            <GraduationCap size={18} strokeWidth={1.75} className="shrink-0 text-blue-700" />
            <span className="truncate font-display text-sm font-semibold">{stats.testTitle}</span>
          </div>
          {examDays !== null && (
            <span className="shrink-0 font-mono text-xs text-slate-600">
              {examDays === 0 ? 'Exam day' : `Exam in ${examDays} day${examDays === 1 ? '' : 's'}`}
            </span>
          )}
        </div>
      )}

      <Card className="flex items-center gap-4">
        <ProgressRing
          value={stats.reviewedToday}
          max={Math.max(1, sessionTotal)}
          size={84}
          thickness={9}
          label="today"
          valueText={
            sessionTotal > 0 ? `${Math.round((stats.reviewedToday / sessionTotal) * 100)}%` : '—'
          }
          className="shrink-0"
        />
        <div className="min-w-0 flex-1 space-y-1">
          {stats.totalCards === 0 ? (
            <>
              <div className="font-display text-lg font-bold">No cards yet</div>
              <p className="text-xs text-slate-600">
                Capture a chapter and generate flashcards to start studying.
              </p>
            </>
          ) : stats.dueCount > 0 ? (
            <>
              <div className="font-display text-lg font-bold">
                <span className="font-mono tabular-nums">{stats.dueCount}</span> card
                {stats.dueCount === 1 ? '' : 's'} due
              </div>
              <p className="text-xs text-slate-600">
                <span className="font-mono tabular-nums">{stats.reviewedToday}</span> of{' '}
                <span className="font-mono tabular-nums">{sessionTotal}</span> reviewed today
              </p>
              <Button variant="primary" size="sm" onClick={() => navigate('/cards')}>
                Start studying
              </Button>
            </>
          ) : (
            <>
              <div className="font-display text-lg font-bold">Nothing due right now</div>
              <p className="text-xs text-slate-600">
                {stats.reviewedToday > 0
                  ? `Nice work — ${stats.reviewedToday} reviewed today. Your next cards unlock tomorrow.`
                  : 'Nice work — your next cards unlock tomorrow.'}
              </p>
            </>
          )}
        </div>
      </Card>

      {stats.chaptersWithDue.length > 0 && (
        <Card padding="sm" className="space-y-1.5">
          <div className="flex items-center gap-1.5 px-1 text-xs font-medium text-slate-700">
            <Layers size={14} strokeWidth={1.75} />
            Due now
          </div>
          <div className="flex flex-wrap gap-1.5 px-1 pb-1">
            {stats.chaptersWithDue.map((c) => (
              <Tag key={c.id} onClick={() => void focusChapter(c.id)} title="Review this chapter">
                <span className="max-w-[16rem] truncate">{c.title}</span>
                <span className="font-mono text-[10px] text-slate-500">{c.dueCount}</span>
              </Tag>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-3 gap-2">
        <Stat
          value={String(stats.streakDays)}
          label={`day streak${stats.streakDays > 0 ? '' : ' — start today'}`}
          icon={
            stats.streakDays > 0 ? (
              <Flame size={14} strokeWidth={1.75} className="text-marker-deep" />
            ) : undefined
          }
        />
        <Stat value={`${masteryPct}%`} label="mastery" />
        <Stat value={String(stats.learnedCards)} label="cards learned" />
      </div>
    </div>
  );
}

function Stat({
  value,
  label,
  icon,
}: {
  value: string;
  label: string;
  icon?: React.ReactNode;
}) {
  return (
    <Card padding="sm" className="text-center">
      <div className="flex items-center justify-center gap-1 font-display text-xl font-bold tabular-nums">
        {icon}
        {value}
      </div>
      <div className="mt-0.5 font-mono text-[10px] uppercase tracking-wide text-slate-500">
        {label}
      </div>
    </Card>
  );
}
